import React, { useRef } from 'react';
import Button from '@mui/material/Button';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';

interface MoveDropdownProps {
  options: string[];
  onSelect: (option: string) => void;
  isOpen: boolean;
  onToggle: () => void;
  triggerLabel?: string;
  triggerIcon?: React.ReactNode;
  triggerClassName?: string;
}

const getStatusBadge = (option: string) => {
  const normalized = option.toLowerCase();
  if (normalized.includes('outstanding')) {
    return { dot: '#a855f7', ring: 'rgba(168, 85, 247, 0.2)' };
  }
  if (normalized.includes('assessment')) {
    return { dot: '#f59e0b', ring: 'rgba(245, 158, 11, 0.2)' };
  }
  if (normalized.includes('shortlisted')) {
    return { dot: '#10b981', ring: 'rgba(16, 185, 129, 0.2)' };
  }
  if (normalized.includes('suitable')) {
    return { dot: '#3b82f6', ring: 'rgba(59, 130, 246, 0.2)' };
  }
  if (normalized.includes('unsuccessful')) {
    return { dot: '#f43f5e', ring: 'rgba(244, 63, 94, 0.2)' };
  }
  if (normalized.includes('new')) {
    return { dot: '#06b6d4', ring: 'rgba(6, 182, 212, 0.2)' };
  }
  if (normalized.includes('psychometric')) {
    return { dot: '#8b5cf6', ring: 'rgba(139, 92, 246, 0.2)' };
  }
  if (normalized.includes('wellness')) {
    return { dot: '#14b8a6', ring: 'rgba(20, 184, 166, 0.2)' };
  }
  return { dot: '#94a3b8', ring: 'rgba(148, 163, 184, 0.2)' };
};

export const MoveDropdown = ({
  options,
  onSelect,
  isOpen,
  onToggle,
  triggerLabel = 'Move',
  triggerIcon,
  triggerClassName,
}: MoveDropdownProps) => {
  const buttonRef = useRef<HTMLButtonElement>(null);

  return (
    <>
      <Button
        ref={buttonRef}
        onClick={onToggle}
        size="small"
        className={triggerClassName}
        startIcon={triggerIcon}
        endIcon={
          <KeyboardArrowDownRoundedIcon
            sx={{
              fontSize: 16,
              transition: 'transform 0.2s',
              transform: isOpen ? 'rotate(180deg)' : 'none',
            }}
          />
        }
        sx={{
          borderRadius: '12px',
          textTransform: 'none',
          fontSize: '0.75rem',
          fontWeight: 600,
          py: 0.75,
          px: 1.5,
          bgcolor: isOpen ? 'rgba(0, 0, 0, 0.05)' : 'background.paper',
          color: 'text.primary',
          border: '1px solid',
          borderColor: isOpen ? 'primary.main' : 'divider',
          boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
          '&:hover': {
            bgcolor: 'action.hover',
            borderColor: 'divider',
          },
        }}
      >
        {triggerLabel}
      </Button>

      <Menu
        anchorEl={buttonRef.current}
        open={isOpen}
        onClose={onToggle}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        PaperProps={{
          elevation: 4,
          sx: {
            width: 250,
            borderRadius: '16px',
            border: '1px solid',
            borderColor: 'divider',
            mt: 0.5,
            p: 0,
            boxShadow: '0 16px 36px -6px rgba(0, 0, 0, 0.14), 0 0 0 1px rgba(0, 0, 0, 0.04)',
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 2,
            pt: 1.5,
            pb: 1,
            borderBottom: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.default',
          }}
        >
          <Typography
            sx={{
              fontSize: '0.625rem',
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              color: 'text.secondary',
            }}
          >
            Move Candidate
          </Typography>
          <Typography sx={{ fontSize: '0.625rem', color: 'text.disabled', fontWeight: 500 }}>
            Select state
          </Typography>
        </Box>

        <Box sx={{ p: 0.75, maxHeight: 320, overflowY: 'auto' }}>
          {options.map((option) => {
            const badge = getStatusBadge(option);
            return (
              <MenuItem
                key={option}
                onClick={() => {
                  onSelect(option);
                  onToggle();
                }}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  borderRadius: '10px',
                  py: 1,
                  px: 1.25,
                  fontSize: '0.75rem',
                  fontWeight: 500,
                  transition: 'all 0.15s ease',
                  '&:hover': {
                    bgcolor: 'action.hover',
                    '& .arrow-icon': {
                      opacity: 1,
                      transform: 'translateX(2px)',
                    },
                  },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
                  <Box
                    component="span"
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      bgcolor: badge.dot,
                      boxShadow: `0 0 0 3px ${badge.ring}`,
                      flexShrink: 0,
                    }}
                  />
                  <Typography
                    component="span"
                    sx={{
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      color: 'text.primary',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    Move to{' '}
                    <Box component="span" sx={{ fontWeight: 700 }}>
                      {option}
                    </Box>
                  </Typography>
                </Box>
                <ArrowForwardRoundedIcon
                  className="arrow-icon"
                  sx={{
                    fontSize: 14,
                    color: 'text.secondary',
                    opacity: 0,
                    transition: 'all 0.15s ease',
                    flexShrink: 0,
                  }}
                />
              </MenuItem>
            );
          })}
        </Box>
      </Menu>
    </>
  );
};


